import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Hammer, Home, Wrench, PaintBucket, WallpaperIcon, Settings } from 'lucide-react';

const serviceIcons = {
  pavers: Hammer,
  kitchen: Home,
  bathroom: Wrench,
  interior: PaintBucket,
  drywall: WallpaperIcon,
  general: Settings,
};

export const ServicesOverview = () => {
  const { t } = useTranslation();

  const services = [
    {
      key: 'pavers',
      title: t('services.pavers.title'),
      description: t('services.pavers.description'),
      link: '/pavers',
      icon: serviceIcons.pavers,
      featured: true,
    },
    {
      key: 'kitchen',
      title: t('services.kitchen.title'),
      description: t('services.kitchen.description'),
      link: '/remodeling#kitchen',
      icon: serviceIcons.kitchen,
    },
    {
      key: 'bathroom',
      title: t('services.bathroom.title'),
      description: t('services.bathroom.description'),
      link: '/remodeling#bathroom',
      icon: serviceIcons.bathroom,
    },
    {
      key: 'interior',
      title: t('services.interior.title'),
      description: t('services.interior.description'),
      link: '/remodeling#interior',
      icon: serviceIcons.interior,
    },
    {
      key: 'drywall',
      title: t('services.drywall.title'),
      description: t('services.drywall.description'),
      link: '/remodeling#drywall',
      icon: serviceIcons.drywall,
    },
    {
      key: 'general',
      title: t('services.general.title'),
      description: t('services.general.description'),
      link: '/remodeling',
      icon: serviceIcons.general,
    },
  ];

  return (
    <section id="services-section" className="py-24 bg-white" data-testid="services-overview-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-block mb-4">
            <span className="text-[hsl(224,64%,33%)] font-bold text-sm uppercase tracking-wider">
              {t('services.ourExpertise')}
            </span>
          </div>
          <h2
            className="text-4xl sm:text-5xl font-black text-[hsl(215,75%,16%)] mb-4 tracking-tight"
            style={{ fontFamily: 'Outfit' }}
          >
            {t('services.title')}
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            {t('services.subtitle')}
          </p>
        </div>

        {/* Featured Service - Pavers */}
        <div className="mb-16">
          <Link
            to="/pavers"
            className="block group"
            data-testid="featured-service-card-pavers"
          >
            <div className="bg-gradient-to-br from-[hsl(215,75%,16%)] to-[hsl(224,64%,33%)] rounded-2xl p-10 md:p-12 text-white premium-card-hover shadow-2xl">
              <div className="flex flex-col md:flex-row items-center gap-8">
                <div className="flex-shrink-0">
                  <div className="w-24 h-24 bg-white/10 backdrop-blur-sm rounded-2xl flex items-center justify-center">
                    <Hammer className="w-12 h-12 text-white" />
                  </div>
                </div>
                <div className="flex-1 text-center md:text-left">
                  <div className="mb-3">
                    <span className="inline-block px-4 py-1 bg-white/20 backdrop-blur-sm rounded-full text-xs font-bold uppercase tracking-wider">
                      {t('services.primaryService')}
                    </span>
                  </div>
                  <h3
                    className="text-3xl md:text-4xl font-black mb-4"
                    style={{ fontFamily: 'Outfit' }}
                  >
                    {t('services.pavers.title')}
                  </h3>
                  <p className="text-xl text-white/90 leading-relaxed mb-6">
                    {t('services.pavers.description')}
                  </p>
                  <div className="inline-flex items-center text-white font-bold group-hover:translate-x-2 transition-transform duration-300">
                    <span className="mr-2">{t('services.learnMore')}</span>
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path d="M9 5l7 7-7 7"></path>
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </Link>
        </div>

        {/* Additional Services Grid */}
        <div>
          <div className="text-center mb-10">
            <h3 className="text-2xl font-bold text-[hsl(215,75%,16%)] mb-2" style={{ fontFamily: 'Outfit' }}>
              {t('services.additionalServices')}
            </h3>
            <p className="text-gray-600">{t('services.additionalServicesSubtitle')}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.slice(1).map((service, index) => {
              const Icon = service.icon;
              return (
                <Link
                  key={service.key}
                  to={service.link}
                  className="premium-card-hover bg-white border-2 border-gray-200 rounded-xl p-8 group hover:border-[hsl(224,64%,33%)]"
                  data-testid={`service-card-${service.key}`}
                >
                  <div className="flex flex-col h-full">
                    {/* Icon */}
                    <div className="mb-6">
                      <div className="w-16 h-16 bg-[hsl(215,75%,16%)] rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                        <Icon className="w-8 h-8 text-white" />
                      </div>
                    </div>

                    {/* Content */}
                    <h3
                      className="text-xl font-bold text-[hsl(215,75%,16%)] mb-3 group-hover:text-[hsl(224,64%,33%)] transition-colors duration-200"
                      style={{ fontFamily: 'Outfit' }}
                    >
                      {service.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed mb-4 flex-grow">
                      {service.description}
                    </p>

                    {/* Learn More Link */}
                    <div className="flex items-center text-[hsl(224,64%,33%)] font-semibold text-sm group-hover:translate-x-2 transition-transform duration-200">
                      <span>{t('services.learnMore')}</span>
                      <svg
                        className="w-4 h-4 ml-2"
                        fill="none"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path d="M9 5l7 7-7 7"></path>
                      </svg>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};